import React, { useState, useEffect } from "react";
import Button from "../../../components/controls/button/Button";
import Draggable from "../../../components/draggable/Draggable";
import styles from "./CustomModal.module.scss";
import { ModalCloseIcon } from "../../../modules/default-svg";
import { createPortal } from "react-dom";
import { FluentProvider, teamsLightTheme } from "@fluentui/react-components";
import MinimalistToggle from "../toggle/MinimalistToggle";

const Modal = ({
  title,
  handleCancel,
  handleSave,
  body,
  isLoading,
  size,
  saveBtnLabel,
  isSaveBtnDisabled,
  cancelBtnLabel,
  type,
  calendarModalWrap,
  footer = true,
  draggable = true,
  zIndex = false,
  width = null,
  additionalFooterButtons = [],
  saveBtnClass,
  checkboxOptions = null, // {id, text, defaultChecked?}
  onConfirmWithOptions = null, // callback for checkbox selections
}) => {
  const [mounted, setMounted] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});

  useEffect(() => {
    // Initialize checkbox states
    if (checkboxOptions) {
      const initialState = {};
      checkboxOptions.forEach(option => {
        initialState[option.id] = option.defaultChecked ?? false;
      });
      setSelectedOptions(initialState);
    }
  }, [checkboxOptions]);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const handleCheckboxChange = (optionId) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionId]: !prev[optionId]
    }));
  };

  const handleConfirm = (e) => {
    if (checkboxOptions && onConfirmWithOptions) {
      const selectedIds = Object.entries(selectedOptions)
        .filter(([_, isSelected]) => isSelected)
        .map(([id]) => id);
      onConfirmWithOptions(selectedIds, selectedOptions);
    } else {
      handleSave(e);
    }
  };

  const modalContent = (
    <Draggable
      draggable={draggable}
      size={size}
      body={
        <div className={`${styles.modalContainer} ${zIndex && "indexing"}`}>
          <div className={styles.modalWrapper}>
            <div
              className={`${styles.modalContent} ${calendarModalWrap && styles.calendarModalWrap
                }  ${size !== undefined
                  ? type
                    ? styles.ExtraLargeModal
                    : size === "large"
                      ? styles.largeModal
                      : styles.smallModal
                  : ""
                }`}
              style={{
                maxHeight:
                  size === "large" &&
                  (window.innerHeight <= 1000
                    ? "calc(100vh - 100px)"
                    : "900px"),
                height: "auto",
                minWidth: width || null,
              }}
            >
              {/* small Large Modal Markup--------------  */}
              <>
                {size !== undefined ? (
                  <>
                    <div className={styles.modalHeader}>
                      <div className="d-flex justify-content-between align-items-center">
                        <h4>{title}</h4>
                        <span
                          onClick={handleCancel}
                          className="d-inline-flex cursor-pointer"
                        >
                          <ModalCloseIcon />
                        </span>
                      </div>
                    </div>
                    <div className={styles.modalbodyWrapper}>
                      <div className={styles.modalbodyContent}>{body}</div>
                    </div>
                    {footer && (
                      <div className={styles.modalFooter}>
                        <div className={styles.btnContainer}>
                          <Button
                            label={cancelBtnLabel}
                            btnClass="d_btn d_btn_whiteMd me-10"
                            btnType="secondary"
                            clickHandler={handleCancel}
                            isDisabled={isLoading}
                          />
                          <Button
                            label={saveBtnLabel}
                            btnClass="d_btn d_btn_primaryMd"
                            clickHandler={handleSave}
                            showLoading={isLoading}
                            isDisabled={isSaveBtnDisabled || isLoading}
                          />
                          {additionalFooterButtons.map((button, index) => (
                            <Button
                              key={index}
                              label={button.label}
                              btnClass={button.btnClass || "d_btn d_btn_primary me-10"}
                              btnType={button.btnType || "primary"}
                              clickHandler={button.onClick}
                              isDisabled={button.isDisabled || isLoading}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  /* confirmation Modal Markup-------------- */
                  <div className={styles.confirmationModal}>
                    <div className="d-flex justify-content-between align-items-center">
                      <h4>{title}</h4>
                      <span
                        onClick={handleCancel}
                        className="d-inline-flex cursor-pointer"
                      >
                        <ModalCloseIcon />
                      </span>
                    </div>
                    <div className={styles.confimationmodalbody}>
                      {body}
                      {checkboxOptions && checkboxOptions.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {checkboxOptions.map((option) => (
                            <div key={option.id} className="flex items-center gap-2">
                              <MinimalistToggle
                                title={option.text}
                                checked={selectedOptions[option.id] || false}
                                onChange={() => handleCheckboxChange(option.id)}
                                disabled={option.isDisabled || isLoading}
                                titlePosition="right"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {footer && (
                      <div className={styles.btnContainer}>
                        {additionalFooterButtons.map((button, index) => (
                          <Button
                            key={index}
                            label={button.label}
                            btnClass={button.btnClass || "d_btn d_btn_primary me-10"}
                            btnType={button.btnType || "primary"}
                            clickHandler={button.onClick}
                            isDisabled={button.isDisabled || isLoading}
                          />
                        ))}
                        <Button
                          label={saveBtnLabel}
                          btnClass={`d_btn d_btn_primary me-10 ${saveBtnClass}`}
                          clickHandler={handleConfirm}
                          showLoading={isLoading}
                          isDisabled={isSaveBtnDisabled || isLoading}
                        />
                        <Button
                          label={cancelBtnLabel}
                          btnClass="d_btn d_btn_smallWhite"
                          btnType="secondary"
                          clickHandler={handleCancel}
                          isDisabled={isLoading}
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            </div>
          </div>
        </div>
      }
    />
  );

  return mounted
    ? createPortal(
      <FluentProvider theme={teamsLightTheme}>{modalContent}</FluentProvider>,
      document.body
    )
    : null;
};

export default Modal;
